<script>
  // Lists a quest's rated submissions (curator picks), Best first. Data comes from
  // the host page's Inertia props; this component only renders and reports intent.
  // `curationLevels` is the server's [{ value, label, definition }] list.
  import dayjs from 'dayjs'
  import localizedFormat from 'dayjs/plugin/localizedFormat'
  import Modal from '../modal.svelte'
  import ClickableImage from '../lightbox/clickable_image.svelte'
  import ClickableVideo from '../lightbox/clickable_video.svelte'
  import { Button } from '../ui'

  dayjs.extend(localizedFormat)

  let {
    open = false,
    quest = null,
    submissions = [],
    curationLevels = [],
    loading = false,
    busyId = null,
    onrate,
    onclear,
    onaddpost,
    onthumbnail,
    onclose
  } = $props()

  const THUMB_CLASS = 'h-12 w-12 object-cover bg-base-200 rounded-box'
</script>

<Modal {open} title="Rated posts" {onclose}>
  {#if quest}
    <p class="text-sm text-base-content/60 mb-4">
      Curator picks for this quest, Best first — shown up front in Quest Detail right after the Example, and eligible
      for the community feed's curated lane.
      <span class="block mt-1 text-base-content">Quest: {quest.cycle_identifier}</span>
      {#if quest.prompt}<span class="block text-base-content">{quest.prompt}</span>{/if}
    </p>

    {#if loading}
      <p class="text-base-content/60 py-3">Loading rated posts…</p>
    {:else if submissions.length === 0}
      <p class="text-base-content/60 py-3">
        No rated posts for this quest yet. Add a curator post below, or rate approved submissions from the Quest
        Submissions page.
      </p>
    {:else}
      <div class="divide-y divide-base-200">
        {#each submissions as submission (submission.id)}
          {@const busy = busyId === submission.id}
          <div class="flex flex-wrap items-center gap-3 py-3">
            {#if submission.moment?.video_url}
              <ClickableVideo url={submission.moment.video_url} thumbnailUrl={submission.moment.thumbnail_url} thumbClass={THUMB_CLASS} />
            {:else if submission.moment?.thumbnail_url}
              <ClickableImage url={submission.moment.thumbnail_url} title="Rated post" thumbClass={THUMB_CLASS} />
            {:else}
              <div class={THUMB_CLASS}></div>
            {/if}
            <div class="grow text-sm">
              <div>{submission.user?.name || 'Unnamed'}</div>
              <!-- "since", not "rated": a seeded post's rating is dated to its Posted at. -->
              <div class="text-xs text-base-content/50">
                {submission.curation_label || 'Unrated'}
                {#if submission.curated_at} · since {dayjs(submission.curated_at).format('ll LT')}{/if}
              </div>
            </div>
            <div class="join">
              {#each curationLevels as level (level.value)}
                {@const active = submission.curation_level === level.value}
                <Button
                  variant={active ? 'warning' : 'default'}
                  soft={active}
                  outline={!active}
                  size="sm"
                  class="join-item"
                  title={level.definition}
                  aria-pressed={active}
                  disabled={active || busy}
                  onclick={() => onrate?.(submission, level.value)}
                >
                  {level.label}
                </Button>
              {/each}
            </div>
            {#if submission.moment?.video_url && onthumbnail}
              <Button
                variant="default"
                outline
                size="sm"
                icon="thumbnail"
                title="Pick a different poster frame for this video"
                onclick={() =>
                  onthumbnail?.({
                    momentId: submission.moment.id,
                    videoUrl: submission.moment.video_url,
                    thumbnailUrl: submission.moment.thumbnail_url
                  })}
              >
                Thumbnail
              </Button>
            {/if}
            <Button
              variant="error"
              outline
              size="sm"
              title="Clear the rating — drops it from the rated posts and the curated lane"
              loading={busy}
              onclick={() => onclear?.(submission)}
            >
              Clear
            </Button>
          </div>
        {/each}
      </div>
    {/if}

    <div class="flex justify-end gap-2 mt-4">
      <Button variant="secondary" outline icon="add" onclick={() => onaddpost?.(quest)}>Add curator post</Button>
      <Button variant="ghost" onclick={onclose}>Close</Button>
    </div>
  {/if}
</Modal>
